import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import { fetchPhaseForms, deletePhaseForm } from "./phaseFormSlice";
import { DataTable, DataFilter } from "@components/ui";
import type { Column } from "@components/ui/DataTable";
import {
  PhaseFormOutputDTO,
  PhaseFormTypeLabels,
  PhaseFormFormatLabels,
} from "./types";
import { LoadingSkeleton } from "@components/ui/LoadingSkeleton";
import { ErrorDisplay } from "@components/ui/ErrorDisplay";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  WhatsApp as WhatsAppIcon,
  QuestionAnswer,
  AttachFile,
  AttachFileSharp,
  AttachFileTwoTone,
  AttachFileOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { filledFormService } from "../filledForm/filledFormService";
import { useNotification } from "@hooks/useNotification";
import {
  FileQuestion,
  MessageCircleQuestion,
  Upload,
  Users,
} from "lucide-react";
import { fetchProducts } from "../Element/productSlice";

const PhaseFormList = (props: {
  hide_add?: boolean;
  clientId?: string;
  productId?: string;
}) => {
  console.log("🔄 Renderizando PhaseFormList");

  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => {
    console.log("🔍 Selector executando, loading:", state.phaseForm.loading);
    return state.phaseForm;
  });
  const {
    products: products,
    loading: loading_products,
    error: error_product,
  } = useSelector((state: RootState) => state.product);

  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const [linkCopied, setLinkCopied] = useState<string | null>(null);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [currentPhaseFormId, setCurrentPhaseFormId] = useState<string | null>(
    null,
  );

  const notification = useNotification();

  const loadData = useCallback(
    async (currentFilters: Record<string, string>) => {
      // Evita chamadas simultâneas
      if (isLoadingRef.current) {
        console.log("🚫 Requisição em andamento, ignorando nova chamada");
        return;
      }

      // Evita chamadas após o carregamento inicial
      if (
        initialLoadDoneRef.current &&
        Object.keys(currentFilters).length === 0
      ) {
        console.log(
          "🚫 Ignorando chamada sem filtros após carregamento inicial",
        );
        return;
      }

      console.log("📡 Iniciando requisição fetchPhaseForms");
      isLoadingRef.current = true;

      try {
        await dispatch(fetchPhaseForms(currentFilters));
        await dispatch(fetchProducts());

        console.log("✅ Requisição fetchPhaseForms completada");
        setIsInitialLoad(false);
        initialLoadDoneRef.current = true;
      } catch (error) {
        console.error("❌ Erro na requisição:", error);
      } finally {
        isLoadingRef.current = false;
      }
    },
    [dispatch],
  );

  const handleFilter = useCallback((newFilters: Record<string, string>) => {
    console.log("🔍 Novos filtros aplicados:", newFilters);
    setFilters(newFilters);
  }, []);

  // Efeito único para carregamento inicial
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      loadData({});
    }
  }, [loadData]);

  // Efeito separado para mudanças nos filtros
  useEffect(() => {
    if (initialLoadDoneRef.current && Object.keys(filters).length > 0) {
      loadData(filters);
    }
  }, [filters, loadData]);

  const handleDelete = async (id: string) => {
    console.log("🗑️ Iniciando delete do item:", id);
    try {
      await dispatch(deletePhaseForm(id));
      setDeleteId(null);
      loadData(filters); // Usando loadData em vez de dispatch direto
    } catch (error) {
      console.error("❌ Erro ao excluir:", error);
    }
  };

  // Copia URL para clipboard com fallback (clipboard API exige HTTPS/localhost +
  // user gesture — fallback via textarea cobre browsers que recusam).
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* cai no fallback */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleGenerateLink = async (phaseFormId: string) => {
    const processId = "new";
    let formUrl = `${window.location.origin}/forms/${phaseFormId}/${processId}`;

    if (props.clientId) {
      const productId = props.productId || "no-product";
      formUrl += `/${productId}/${props.clientId}`;
    } else if (props.productId) {
      formUrl += `/${props.productId}`;
    }

    const ok = await copyToClipboard(formUrl);
    if (ok) {
      notification.showSuccess(`Link completo copiado: ${formUrl}`);
      setLinkCopied(phaseFormId);
      setTimeout(() => setLinkCopied(null), 2000);
    } else {
      notification.showError('Não foi possível copiar o link. Copie manualmente: ' + formUrl);
    }
  };

  const handleGenerateLinkSimple = async (phaseFormId: string) => {
    const processId = "new";
    let formUrl = `${window.location.origin}/first-forms/${phaseFormId}/${processId}`;

    if (props.clientId) {
      const productId = props.productId || "no-product";
      formUrl += `/${productId}/${props.clientId}`;
    } else if (props.productId) {
      formUrl += `/${props.productId}`;
    }

    const ok = await copyToClipboard(formUrl);
    if (ok) {
      notification.showSuccess(`Link simples copiado: ${formUrl}`);
      setLinkCopied(phaseFormId);
      setTimeout(() => setLinkCopied(null), 2000);
    } else {
      notification.showError('Não foi possível copiar o link. Copie manualmente: ' + formUrl);
    }
  };

  const handlePhoneSubmit = async () => {
    if (!currentPhaseFormId) return;

    // Process phone numbers and add +55 if country code is missing
    const phones = phoneInput.split(",").map((phone) => {
      const trimmedPhone = phone.trim();
      // If phone doesn't start with +, add +55
      return trimmedPhone.startsWith("+") ? trimmedPhone : `+55${trimmedPhone}`;
    });

    // RESTAURACAO: subaccount removido — o BE ja resolve via env TWILIO_OWNER_ACCOUNT_SID
    // (default = AC487af6... CoverPay real). O SID hardcoded antigo (AC9d5e...) era de
    // outra subaccount e nem era usado pelo BE no path direto.
    const payload = {
      phones,
      phaseForm: currentPhaseFormId,
      phaseFormId: currentPhaseFormId,
    };

    try {
      await filledFormService.submitPhones(payload);
      console.log("Phone numbers submitted successfully");
      notification.showSuccess("Números de WhatsApp enviados com sucesso!");
      setPhoneModalOpen(false);
      setPhoneInput("");
    } catch (error) {
      console.error("Error submitting phone numbers:", error);
      notification.showError(
        "Erro ao enviar números de WhatsApp. Tente novamente.",
      );
    }
  };

  const filterFields = [
    {
      name: "name",
      label: "Nome",
      type: "text" as const,
    },
    {
      name: "description",
      label: "Descrição",
      type: "text" as const,
    },
  ];

  const columns: Column<PhaseFormOutputDTO>[] = [
    {
      key: "name",
      header: "Nome",
    },
    {
      key: "description",
      header: "Descrição",
    },
    {
      key: "formFields",
      header: "Campos",
      render: (value: PhaseFormOutputDTO["formFields"]) => {
        return value?.length ? `${value.length} campo(s)` : "Nenhum";
      },
    },
    {
      key: "type",
      header: "Tipo",
      render: (value: PhaseFormOutputDTO["type"]) =>
        PhaseFormTypeLabels[value as keyof typeof PhaseFormTypeLabels],
    },
    {
      key: "format",
      header: "Exibição",
      render: (value: PhaseFormOutputDTO["format"]) =>
        PhaseFormFormatLabels[value as keyof typeof PhaseFormFormatLabels] ||
        "Completo",
    },
    {
      key: "hasAppointment",
      header: "Agendamento",
      render: (value: boolean, item: PhaseFormOutputDTO) => {
        return value ? (
          <div className="flex items-center">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
            Sim
          </div>
        ) : (
          "Não"
        );
      },
    },
  ];
  const [allPhases, setAllPhases] = useState([]);
  // const [allProducts, setAllProducts] = useState([]);
  const [allActivities, setAllActivities] = useState([]);

  const [selectedPhaseFilter, setSelectedPhaseFilter] =
    useState<string>("todas");
  const [selectedProductFilter, setSelectedProductFilter] =
    useState<string>("todas");
  const [selectedActivityFilter, setSelectedActivityFilter] =
    useState<string>("todas");

  if (isInitialLoad && loading) {
    return <LoadingSkeleton />;
  }
  console.log("items", items);

  return (
    <Box sx={{ p: 3 }}>
      {/* <Typography variant="h5" gutterBottom>
        Formulários
      </Typography> */}

      {error && <ErrorDisplay error={error} />}

      {/* <DataFilter
        fields={filterFields}
        onFilter={handleFilter}
        autoSubmit={true}
        debounceTime={500}
      /> */}

      <DataTable<PhaseFormOutputDTO>
        hide_header={props.hide_add != undefined}
        hide_add={props.hide_add != undefined}
        hideCrud={props.hide_add != undefined ? true : undefined}
        children={() =>
          props.hide_add == undefined && (
            <div
              className="mb-4 flex w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
              style={{
                position: "relative",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ minWidth: "33%" }}
                className="w-full sm:w-[50%] md:w-[33%]"
              >
                <label className="text-sm font-semibold text-gray-700">
                  Produto ({products?.length})
                </label>
                <select
                  name="product_select"
                  value={selectedProductFilter}
                  onChange={(e) => {
                    console.log(e.target.value);
                    setSelectedProductFilter(e.target.value);
                    if (e.target.value != "todas") {
                      let product: any = products.filter(
                        (item) => item._id == e.target.value,
                      )[0];
                      setAllPhases(product.phases);
                    } else {
                      setAllPhases([]);
                    }
                    setSelectedPhaseFilter("todas");
                    setSelectedActivityFilter("todas");
                    setAllActivities([]);
                  }}
                  required
                  style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                  className={`mt-1 h-12 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
                >
                  <option value={"todas"}>Todas os Produtos</option>
                  {/* <option value={'Geral'}>
                  Geral
                </option>
                <option value={'Comercial'}>
                  Comercial
                </option> */}

                  {products?.map((product: any) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-[50%] md:w-[33%]">
                <label className="text-sm font-semibold text-gray-700">
                  Fase ({allPhases?.length})
                </label>
                <select
                  name="phase_select"
                  value={selectedPhaseFilter}
                  onChange={(e) => {
                    console.log(e.target.value);
                    setSelectedPhaseFilter(e.target.value);

                    console.log(e.target.value);
                    if (e.target.value != "todas") {
                      let product: any = products.filter(
                        (item) => item._id == selectedProductFilter,
                      )[0];
                      let phase = product.phases.filter(
                        (item) => item._id == e.target.value,
                      )[0];
                      setAllActivities(phase.activities);
                    } else {
                      setAllActivities([]);
                    }
                    setSelectedActivityFilter("todas");
                  }}
                  required
                  style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                  className={`mt-1 h-12 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
                >
                  <option value={"todas"}>Todas as fases</option>
                  {/* <option value={'Geral'}>
                  Geral
                </option>
                <option value={'Comercial'}>
                  Comercial
                </option> */}

                  {allPhases?.map((product: any) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-[50%] md:w-[33%]">
                <label className="text-sm font-semibold text-gray-700">
                  Atividade ({allActivities?.length})
                </label>
                <select
                  name="activity_select"
                  value={selectedActivityFilter}
                  onChange={(e) => {
                    console.log(e.target.value);
                    setSelectedActivityFilter(e.target.value);
                  }}
                  required
                  style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                  className={`mt-1 h-12 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
                >
                  <option value={"todas"}>Todas as atividades</option>
                  {/* <option value={'Geral'}>
                  Geral
                </option>
                <option value={'Comercial'}>
                  Comercial
                </option> */}

                  {allActivities?.map((product: any) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )
        }
        title="Formulários"
        subtitle="Lista de formulários cadastrados no sistema"
        data={items.map((item: any) => {
          return {
            _id: item._id,
            title: item.name,
            name: item.name,

            icon_type: <Users size={16} />,
            type: item.type == "OPENED" ? "Aberto" : "Fechado",
            stats: [
              {
                icon: <MessageCircleQuestion size={16} />,
                name: "Perguntas",
                count: item.formFields.filter(
                  (item2: any) => item2.acceptFiles == false,
                ).length,
              },
              {
                icon: <AttachFileOutlined fontSize="16" />,
                name: "Uploads docs",
                count: item.formFields.filter(
                  (item2: any) => item2.acceptFiles == true,
                ).length,
              },
            ],
          };
        })}
        columns={columns}
        formatGrid
        basePath="phase-forms"
        onDelete={(id) => {
          console.log("🗑️ Solicitação de delete para id:", id);
          setDeleteId(id);
        }}
        loading={loading}
        error={error}
        loadingComponent={<LoadingSkeleton title={false} filter={false} />}
        actions={[
          // {
          //   label: "Preview",
          //   icon: <VisibilityIcon />,
          //   onClick: (item: PhaseFormOutputDTO) =>
          //     navigate(`/form-wizard/${item._id}/preview`),
          // },
          {
            label: "Gerar Link Completo",
            icon: <LinkIcon />,
            onClick: (item: PhaseFormOutputDTO) =>
              handleGenerateLink(item._id || ""),
          },
          {
            label: "Gerar Link Simples",
            icon: <LinkIcon />,
            onClick: (item: PhaseFormOutputDTO) =>
              handleGenerateLinkSimple(item._id || ""),
          },
          {
            label: "Enviar WhatsApp",
            icon: <WhatsAppIcon />,
            onClick: (item: PhaseFormOutputDTO) => {
              setCurrentPhaseFormId(item._id || null);
              setPhoneModalOpen(true);
            },
          },
        ]}
      />

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este formulário de fase?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={phoneModalOpen} onClose={() => setPhoneModalOpen(false)}>
        <DialogTitle>Enviar WhatsApp</DialogTitle>
        <DialogContent>
          <Typography>
            Insira uma lista de números de telefone separados por vírgula:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Digite apenas DDD+NÚMERO (ex: 11987654321) ou com código do país
            (ex: +5511987654321). O código +55 (Brasil) será adicionado
            automaticamente se não for informado.
          </Typography>
          <textarea
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            style={{ width: "100%", height: "100px", marginTop: "8px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={handlePhoneSubmit}
            color="primary"
            variant="contained"
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhaseFormList;
